// Revisado: 2025-12-24 [I18n Scan]
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

// --- Logic Helpers ---

const getRarityWeight = (rarity) => {
    if (rarity === 'SSR') return 3;
    if (rarity === 'SR') return 2;
    if (rarity === 'R') return 1;
    return 0;
};



const SupportsBests = ({ data, inventory = {}, isGuest = false }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    // Rarity Toggles State
    // "Los botones funcionan, Activan uno Se desactiva el resto, Presionas uno Activo, se desactiva."
    // "Solo si los 3 estan desactivados se muestran versiones cruzadas"
    const [activeRarity, setActiveRarity] = useState(null); // 'SSR' | 'SR' | 'R' | null
    const [selectedRegion, setSelectedRegion] = useState('jp'); // 'jp' (All) | 'global' 
    const [selectedCap, setSelectedCap] = useState(4); // 0-4 


    const handleRarityToggle = (rarity) => {
        if (activeRarity === rarity) {
            setActiveRarity(null);
        } else {
            setActiveRarity(rarity);
        }
    };

    // --- The Heavy Calculation ---
    // "Extremadamente preciso... calculo complicado... trabajo de relojeria"
    const bestLists = useMemo(() => {
        if (!data || !data.matrix) return null;

        // 1. Initial Pool Setup (Contextual Scope)
        // If a rarity filter is active, we ONLY analyze that pool.
        // This ensures "Tier S" (Top 10%) is calculated relative to the peer group.
        let allCards = Object.entries(data.matrix).map(([id, card]) => ({ ...card, id }));

        // Region Filter or Inventory Filter
        if (selectedRegion === 'global') {
            allCards = allCards.filter(c => c.info.release_en);
        } else if (selectedRegion === 'inventory') {
            // Filter by OWNERSHIP
            allCards = allCards.filter(c => {
                const id = c.id;
                // Robust Check (String vs Number)
                const val = inventory[Number(id)] !== undefined ? inventory[Number(id)] : inventory[id];
                return val !== undefined;
            });
        }

        if (activeRarity) {
            allCards = allCards.filter(c => c.info.rarity === activeRarity);
        }

        const scores = {}; // { cardId: totalScore }

        // 2. Get All Unique Effects to iterate
        const allEffects = new Set();
        // Iterate only over the filtered cards to avoid analyzing effects that don't exist in this rarity
        allCards.forEach(card => {
            Object.keys(card.effects).forEach(eff => allEffects.add(eff));
        });
        const effectList = Array.from(allEffects);

        // 3. Iterate EVERY Effect
        // 3. Iterate EVERY Effect
        effectList.forEach(effectKey => {

            // a. Calculate Values using "Effective Cap" Logic
            let rawEffectCards = allCards.map(card => {
                const normalValues = card.effects[effectKey] || [0, 0, 0, 0, 0];

                // Add Unique Bonus
                let uniqueBonusVal = 0;
                if (card.unique_effects) {
                    const match = card.unique_effects.find(u => u.description === effectKey);
                    if (match) {
                        const parsed = parseFloat(match.value);
                        uniqueBonusVal = isNaN(parsed) ? 0 : parsed;
                    }
                }

                // Determine Active Cap
                let activeCap = selectedCap; // Default to User Selection
                if (selectedRegion === 'inventory') {
                    const cardId = card.id;
                    const invVal = inventory[Number(cardId)] !== undefined ? inventory[Number(cardId)] : (inventory[cardId] !== undefined ? inventory[cardId] : -1);
                    // If not owned, effective cap is -1 (invalid)
                    activeCap = invVal;
                }

                if (activeCap < 0) return null; // Logic: User doesn't own it -> Exclude from scoring

                const values = normalValues.map(v => {
                    const clean = parseFloat(v);
                    return (isNaN(clean) ? 0 : clean) + uniqueBonusVal;
                });

                // The FINAL POWER of this card is the value at its Active Cap.
                const currentPower = values[activeCap];

                // Optimization: Ignore cards providing 0 value
                if (currentPower === 0) return null;

                return {
                    ...card,
                    currentPower, // The single number that matters
                    effectKey
                };
            }).filter(c => c !== null);

            if (rawEffectCards.length === 0) return;

            // --- FILTER: MINIMUM STANDARD ---
            // If the BEST card gives 20, and I give 1, I shouldn't be here cluttering the view.
            const maxPowerInPool = Math.max(...rawEffectCards.map(c => c.currentPower));
            const THRESHOLD = maxPowerInPool * 0.15;

            // Only keep relevant cards
            const currentEffectCards = rawEffectCards.filter(c => c.currentPower >= THRESHOLD);

            if (currentEffectCards.length === 0) return;

            // b. SCORING: PURE POWER PERCENTAGE
            // "Score related only to what it HAS NOW"

            const WEIGHT_PER_EFFECT = 100;
            // reused maxPowerInPool from line 123

            currentEffectCards.forEach(c => {
                const rel = c.currentPower / maxPowerInPool;
                // Score = Relative Power * Weight (100)
                const points = rel * WEIGHT_PER_EFFECT;
                addScore(c.id, points, effectKey);
            });

            function addScore(id, amount, label) {
                scores[id] = scores[id] || { total: 0, breakdown: {} };
                scores[id].total += amount;
                const niceLabel = label.replace(/_/g, ' ');
                scores[id].breakdown[niceLabel] = (scores[id].breakdown[niceLabel] || 0) + amount;
            }

        });

        // 4. Final Aggregation
        const grouped = {
            Speed: [], Stamina: [], Power: [], Guts: [], Wit: [], Pal: [], Group: []
        };

        allCards.forEach(card => {
            let scoreData = scores[card.id];
            if (scoreData && scoreData.total > 0) {
                let finalScore = scoreData.total;

                // Multiplier
                let multiplier = 1.0;
                if (card.info.rarity === 'SR') multiplier = 0.85;
                if (card.info.rarity === 'R') multiplier = 0.60;

                finalScore = Math.round(finalScore * multiplier);

                const type = card.info.Support_Type;
                const normType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

                // Format Breakdown for Tooltip
                const breakdownStr = Object.entries(scoreData.breakdown)
                    .map(([k, v]) => `${k}: ${Math.round(v)}%`)
                    .join(', ');

                if (grouped[normType]) {
                    grouped[normType].push({
                        ...card,
                        totalScore: finalScore,
                        scoreDetails: breakdownStr,
                        scoreBreakdown: scoreData.breakdown // Pass raw object for custom tooltip
                    });
                }
            }
        });

        // 5. Sort
        Object.keys(grouped).forEach(key => {
            // Sort by Score Desc, then Rarity
            grouped[key].sort((a, b) => {
                if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
                return getRarityWeight(b.info.rarity) - getRarityWeight(a.info.rarity);
            });
        });

        return grouped;

    }, [data, activeRarity, selectedRegion, inventory, selectedCap]); // Depend on activeRarity to trigger re-calc relative to the pool

    // Apply Display Filters (Rarity)
    const displayLists = useMemo(() => {
        if (!bestLists) return null;

        const res = {};
        Object.keys(bestLists).forEach(type => {
            let list = bestLists[type];

            if (activeRarity) {
                list = list.filter(c => c.info.rarity === activeRarity);
            }

            // Limit: 5 for all categories
            const limit = 5;
            res[type] = list.slice(0, limit);
        });
        return res;
    }, [bestLists, activeRarity]);


    // Helper for rendering a small card item
    const BestCardItem = ({ card, rank }) => {
        const [showTooltip, setShowTooltip] = useState(false);

        return (
            <div
                className="flex items-center gap-3 bg-slate-100 dark:bg-slate-700/50 p-2 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-700 transition-colors group relative cursor-help"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <div className="relative w-10 h-10 flex-shrink-0">
                    <img
                        src={`${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${card.id}_Icon.png&width=64`}
                        className="w-full h-full rounded shadow-sm object-cover"
                        alt={card.info.name}
                        loading="lazy"
                    />
                    <div className="absolute -top-2 -left-2 w-5 h-5 flex items-center justify-center bg-slate-800 text-white text-[10px] font-bold rounded-full border border-slate-600 shadow z-10">
                        {rank}
                    </div>
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate w-24">
                        {card.info.char_name || card.info.name}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <span className={`font-bold ${card.info.rarity === 'SSR' ? 'text-rainbow' : ''}`}>{card.info.rarity}</span>
                        <span>Score: <span className="text-cyan-600 dark:text-cyan-400 font-mono">{card.totalScore}</span></span>
                    </div>
                </div>

                {/* Custom Tooltip */}
                {showTooltip && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-slate-900/95 backdrop-blur-sm text-white text-[10px] rounded-lg shadow-xl border border-slate-700 p-2 z-50 pointer-events-none">
                        <div className="font-bold border-b border-slate-700 pb-1 mb-1 text-slate-300">
                            Score Contribution
                        </div>
                        <div className="space-y-1">
                            {Object.entries(card.scoreBreakdown || {}).sort((a, b) => b[1] - a[1]).map(([key, val]) => (
                                <div key={key} className="flex justify-between items-center">
                                    <span className="text-slate-400 capitalize truncate max-w-[70%]">{key}</span>
                                    <span className="font-mono text-cyan-400">{Math.round(val)}%</span>
                                </div>
                            ))}
                        </div>
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45 border-r border-b border-slate-700"></div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg transition-all mb-8 ${!data ? 'opacity-70' : ''}`}>
            <button
                onClick={() => data && setIsOpen(!isOpen)}
                className={`w-full flex justify-between items-center p-4 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-900 hover:from-slate-100 dark:hover:from-slate-800 transition-all ${!data ? 'cursor-not-allowed' : 'cursor-pointer'} ${isOpen ? 'rounded-t-xl' : 'rounded-xl'}`}
            >
                <div className="flex items-center gap-2">
                    <div className="bg-orange-500 text-white p-1.5 rounded-lg shadow-sm">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">
                            {t('SupportsBests.title')}
                        </h3>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">
                            {t('SupportsBests.subtitle')}
                        </p>
                    </div>
                </div>
                <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''} text-slate-400`}>
                    ▼
                </span>
            </button>

            {isOpen && displayLists && (
                <div className="p-6 bg-slate-50 dark:bg-slate-900/30 border-t border-slate-200 dark:border-slate-700 space-y-6 rounded-b-xl">

                    <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-6">
                        {/* Region Filter */}
                        <div className="flex bg-slate-100 dark:bg-slate-900 rounded-lg p-1 border border-slate-200 dark:border-slate-700 w-fit">
                            <button
                                onClick={() => setSelectedRegion('jp')}
                                className={`px-4 py-2 rounded-md font-bold text-sm transition-all ${selectedRegion === 'jp'
                                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                                    : 'text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                {t('SupportsBests.btn_jp')}
                            </button>
                            <button
                                onClick={() => setSelectedRegion('global')}
                                className={`px-4 py-2 rounded-md font-bold text-sm transition-all ${selectedRegion === 'global'
                                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                                    : 'text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                            >
                                {t('SupportsBests.btn_global')}
                            </button>
                            <button
                                onClick={() => !isGuest && setSelectedRegion('inventory')}
                                disabled={isGuest}
                                className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${selectedRegion === 'inventory'
                                    ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 shadow-sm'
                                    : isGuest
                                        ? 'text-slate-300 dark:text-slate-700 cursor-not-allowed'
                                        : 'text-slate-500 dark:text-slate-500 hover:text-purple-600 dark:hover:text-purple-400'
                                    }`}
                                title={isGuest ? "Login Required" : ""}
                            >
                                <span>📦</span> {t('SupportFilter.btn_only_inventory') || "My Inventory"}
                            </button>
                        </div>

                        {/* CAP Selector (Hidden in Inventory Mode) */}
                        {selectedRegion !== 'inventory' && (
                            <div className="flex bg-slate-100 dark:bg-slate-900 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                                {[0, 1, 2, 3, 4].map(cap => (
                                    <button
                                        key={cap}
                                        onClick={() => setSelectedCap(cap)}
                                        className={`w-10 h-8 rounded-md font-bold text-sm transition-all ${selectedCap === cap
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'text-slate-500 hover:text-blue-500'
                                            }`}
                                        title={`Limit Break ${cap}`}
                                    >
                                        {cap}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Rarity Controls */}
                        <div className="flex gap-4">
                            {['SSR', 'SR', 'R'].map(r => (
                                <button
                                    key={r}
                                    onClick={() => handleRarityToggle(r)}
                                    className={`px-6 py-2 rounded-full font-bold text-sm transition-all border ${activeRarity === r
                                        ? 'bg-orange-500 text-white border-orange-600 shadow-lg scale-105'
                                        : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-600 hover:border-orange-300 dark:hover:border-orange-700'
                                        }`}
                                >
                                    {t('SupportsBests.btn_rarity_only', { rarity: r })}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {/* Render Lists */}
                        {['Speed', 'Stamina', 'Power', 'Guts', 'Wit', 'Pal', 'Group'].map(type => {
                            const list = displayLists[type];
                            // Skip empty lists
                            if (!list || list.length === 0) return null;

                            const iconPath = ['Pal', 'Group'].includes(type)
                                ? `/images/Icon/Support_Icons/Support_Type_${type}.png`
                                : `/images/Icon/Stats_icons/Stat_Type_${type}.png`;

                            return (
                                <div key={type} className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col gap-3">
                                    <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-2">
                                        <img src={iconPath} className="w-6 h-6 object-contain" alt={type} />
                                        <h4 className="font-bold text-slate-700 dark:text-slate-300 uppercase text-sm">Best {type}</h4>
                                    </div>

                                    <div className="space-y-2">
                                        {list.map((card, idx) => (
                                            <BestCardItem key={card.id} card={card} rank={idx + 1} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>
            )}
        </div>
    );
};

export default SupportsBests;
